import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PLayout from "./PLayout";
import SessionBlocks from "@/components/SessionBlocks";
import { Part2FormGroup, entryKeys, formToPdf, pdfDownload } from "@/components/part2/Part2Form";
import { Part2Overview, Part1Recap } from "@/components/part2/Part2Overview";
import { useApp } from "@/context/AppContext";
import { useAppUi } from "@/i18n/appUi";
import { getUi } from "@/portal/portal";
import { myPart2, myPart2Responses, getPortal } from "@/lib/api";
import { CHECKIN_FORM, EVALUATION_FORM, localize } from "@/data/part2Content";
import { pick, fmtDate, getDayContent, canOpenDay } from "./part2Util";

const TABS = ["overview", "checkin", "theory", "quiz", "forms", "homework", "eval"];
const TAB_KEY = { overview: "tabOverview", checkin: "tabCheckin", theory: "tabTheory", quiz: "tabQuiz", forms: "tabForms", homework: "tabHomework", eval: "tabEval" };

function Empty({ t }) {
  return <p className="rbl-intro" style={{ marginTop: 24 }} data-testid="part2-empty">{t("p2Empty")}</p>;
}

function TrainerExtras({ day, lang, t }) {
  const secs = (day.sections || []).filter((s) => pick(s.heading, lang) || pick(s.body, lang));
  if (!secs.length) return null;
  return (
    <div style={{ marginTop: 28 }} data-testid="part2-trainer-extras">
      <h2 className="rbl-block-h">{t("extraTrainer")}</h2>
      {secs.map((s, i) => (
        <div className="rbl-feature" style={{ marginTop: 14 }} key={i} data-testid={`part2-section-${i}`}>
          {pick(s.heading, lang) && <h3>{pick(s.heading, lang)}</h3>}
          {pick(s.body, lang).split(/\n\s*\n/).map((p, j) => <p key={j} style={{ whiteSpace: "pre-line" }}>{p}</p>)}
        </div>
      ))}
    </div>
  );
}

function AllFormsPdf({ list, forms, pdfMeta, t }) {
  const filled = () => list.flatMap((f) => entryKeys(forms, f.id).map((k) =>
    formToPdf({ ...f, title: k === f.id ? f.title : `${f.title} (${k.split("__")[1]})` }, forms[k]?.answers || {})));
  return (
    <div className="rbl-feature" style={{ marginTop: 18 }} data-testid="p2-pdf-all">
      <h3>⬇ {t("pdfAllTitle")}</h3>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
        <button className="rbl-btn" data-testid="p2-pdf-all-blank" onClick={() => pdfDownload(pdfMeta, list.map((f) => formToPdf(f)), true, t)}>{t("pdfAllBlank")}</button>
        <button className="rbl-btn green" data-testid="p2-pdf-all-filled" onClick={() => pdfDownload(pdfMeta, filled(), false, t)}>{t("pdfAllFilled")}</button>
      </div>
    </div>
  );
}

function TabBody({ tab, n, day, content, forms, setForms, lang, t, meta }) {
  const tp = getUi(lang);
  const onChange = (id, answers) => setForms((s) => ({ ...s, [id]: { ...s[id], answers, saved: false } }));
  const onSaved = (id) => setForms((s) => ({ ...s, [id]: { ...s[id], saved: true } }));
  const pdfMeta = { title: pick(day.title, lang), subtitle: `${t("navPart2")} · ${t("dayLabel")} ${n}`, kicker: t("p2Kicker") };
  const group = (f, multi = true) => (
    <Part2FormGroup key={f.id} day={n} form={f} forms={forms} setForms={setForms} multi={multi} onChange={onChange} onSaved={onSaved} t={t} pdfMeta={pdfMeta} />
  );
  const extra = (sec, i) => {
    if (sec.kind === "topic") return <h2 key={i} className="rbl-sess-title" style={{ fontSize: 28, marginTop: 40, borderTop: "2px solid var(--green)", paddingTop: 18 }}>{sec.title}</h2>;
    if (sec.kind === "part1-recap") return <Part1Recap key={i} lang={lang} t={t} sessions={sec.sessions} meta={meta} />;
    if (sec.kind === "quiz-ref") return <SessionBlocks key={i} sections={[content.quiz[sec.idx]]} t={tp} />;
    if (sec.kind === "form-ref") {
      const f = content.forms.find((x) => x.id === sec.id);
      return f ? <div key={i}>{group(f)}</div> : null;
    }
    return null;
  };
  if (tab === "checkin") return group(localize(CHECKIN_FORM, lang), false);
  if (tab === "eval") return group(localize(EVALUATION_FORM, lang), false);
  if (tab === "theory") return (
    <>
      {content ? <SessionBlocks sections={content.theory} metaBySession={meta} t={tp} renderExtra={extra} /> : null}
      <TrainerExtras day={day} lang={lang} t={t} />
      {!content && !(day.sections || []).length && <Empty t={t} />}
    </>
  );
  if (!content) return <Empty t={t} />;
  if (tab === "overview") return <Part2Overview ov={content.overview} t={t} />;
  if (tab === "quiz") return (
    <>
      <p className="rbl-intro" style={{ marginTop: 18 }}>{t("selfTestHint")}</p>
      <SessionBlocks sections={content.quiz} t={tp} />
    </>
  );
  if (tab === "forms") return (
    <>
      <AllFormsPdf list={[...content.forms, ...content.homework]} forms={forms} pdfMeta={pdfMeta} t={t} />
      {content.forms.map((f) => group(f))}
    </>
  );
  if (tab === "homework") return (
    <>
      <AllFormsPdf list={content.homework} forms={forms} pdfMeta={pdfMeta} t={t} />
      {content.homework.map((f) => group(f))}
    </>
  );
  return null;
}

export default function Part2Day() {
  const { day } = useParams();
  const n = parseInt(day, 10);
  const { lang, user } = useApp();
  const t = useAppUi(lang);
  const [data, setData] = useState(null);
  const [forms, setForms] = useState(null);
  const [meta, setMeta] = useState({});
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    window.scrollTo(0, 0);
    setTab("overview");
    myPart2().then(setData).catch(() => setData({ access: false, days: [] }));
    const toState = (r) => Object.fromEntries(Object.entries(r).map(([k, v]) => [k, { answers: v.answers || {}, saved: true }]));
    myPart2Responses(n).then((r) => setForms(toState(r))).catch(() => setForms({}));
    getPortal().then((p) => setMeta(Object.fromEntries((p.sessions || []).map((s) => [s.number, s])))).catch(() => {});
  }, [n]);

  useEffect(() => {
    const dirty = forms && Object.values(forms).some((f) => f.saved === false && Object.keys(f.answers || {}).length);
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    if (dirty) window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [forms]);

  const d = data?.days?.find((x) => x.day_number === n);
  const open = canOpenDay(n, user?.role);
  const content = getDayContent(n, lang);
  const go = (k) => { setTab(k); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const next = TABS[TABS.indexOf(tab) + 1];

  return (
    <PLayout>
      <Link to="/app/part2" className="rbl-back" data-testid="part2-back">{t("p2Back")}</Link>
      {data && !data.access && <p className="rbl-intro" data-testid="part2-no-access">{t("p2NoAccess")}</p>}
      {data?.access && !d && <h1 className="rbl-h1" data-testid="part2-not-found">404</h1>}
      {d && !open && <p className="rbl-intro" style={{ marginTop: 24 }} data-testid="part2-locked">🔒 {t("lockedSoon")}</p>}
      {d && open && (
        <div data-testid={`part2-day-page-${n}`}>
          <div style={{ marginTop: 18 }} className="rbl-sess-eyebrow">{t("navPart2")} · {t("dayLabel")} {String(n).padStart(2, "0")}</div>
          <h1 className="rbl-sess-title">{pick(d.title, lang)}</h1>
          <div className="rbl-sess-sub">{pick(d.subtitle, lang)}</div>
          <div className="rbl-card-meta" style={{ marginTop: 8 }} data-testid="part2-day-hours">
            🕘 {d.hours}{data.dates?.[n - 1] ? ` · ${fmtDate(data.dates[n - 1], lang)}` : ""}
          </div>
          <div className="rbl-p2-tabs" role="tablist" data-testid="part2-tabs">
            {TABS.map((k, i) => (
              <button key={k} role="tab" className={tab === k ? "active" : ""} data-testid={`part2-tab-${k}`} onClick={() => go(k)}>
                <span className="n">{i + 1}</span>{t(TAB_KEY[k])}
              </button>
            ))}
          </div>
          {forms && <TabBody tab={tab} n={n} day={d} content={content} forms={forms} setForms={setForms} lang={lang} t={t} meta={meta} />}
          <div className="rbl-sess-nav">
            <span />
            {next
              ? <button className="rbl-btn" onClick={() => go(next)} data-testid="part2-next-tab">{t(TAB_KEY[next])} →</button>
              : <Link to="/app/part2" className="rbl-btn" data-testid="part2-finish">{t("p2Back")}</Link>}
          </div>
        </div>
      )}
    </PLayout>
  );
}
